import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { InfoApi } from "../../api/InfoApi";
import { OrgAdminApi } from "../../api/OrgAdminApi";
import { backgroundOption } from "../../components/leaderboardSetting/BackgroundSelection";
import LeaderboardPreviewDialog from "../../components/leaderboardSetting/LeaderboardPreviewDialog";
import ReactSwal from "../../helper/AlertHelper";
import { parseLeaderboardCode } from "../../helper/leaderboardPreview";
import { openLeaderboardDisplay } from "../../helper/leaderboardSlide";

const SCOPE_LABEL = {
  organization: "本校",
  steminpe: "校際",
};

const GROUP_BY_LABEL = {
  member: "學生",
  school_cls: "班別",
  grp: "社",
  organization: "學校",
};

const ITEM_LABEL = {
  duration: "累計時長",
  score: "最佳成績",
};

function asList(data) {
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data?.data)) {
    return data.data;
  }
  return [];
}

function scopeLabel(parsed) {
  if (!parsed) {
    return "-";
  }
  if (parsed.scope === "form" || parsed.scope === "grade") {
    return parsed.scopeArg && parsed.scopeArg !== "_" ? `${parsed.scopeArg} 年級` : "年級";
  }
  if (parsed.scope === "grp") {
    return parsed.scopeArg && parsed.scopeArg !== "_" ? parsed.scopeArg : "社";
  }
  return SCOPE_LABEL[parsed.scope] || parsed.scope || "-";
}

function groupByLabel(parsed) {
  if (!parsed) {
    return "-";
  }
  return GROUP_BY_LABEL[parsed.groupBy] || parsed.groupBy || "-";
}

function itemLabel(parsed) {
  if (!parsed) {
    return "-";
  }
  return ITEM_LABEL[parsed.item] || parsed.item || "-";
}

function skillLabel(parsed, skillNameByCode) {
  if (!parsed) {
    return "-";
  }
  if (!parsed.skillCode || parsed.skillCode === "all") {
    return "全部活動";
  }
  return skillNameByCode[parsed.skillCode] || parsed.skillCode;
}

export function confirmDeleteLeaderboardSetting(id, onDeleted) {
  ReactSwal.fire({
    title: "確定要刪除嗎？",
    icon: "warning",
    showConfirmButton: true,
    showCancelButton: true,
    confirmButtonText: "確定",
    cancelButtonText: "取消",
    cancelButtonColor: "#a7a7a7",
  }).then((result) => {
    if (!result.isConfirmed) {
      return;
    }

    OrgAdminApi.deleteLeaderboardSettingCallback(id, (isSuccess, msg) => {
      if (!isSuccess) {
        toast.error(typeof msg === "string" && msg ? msg : "刪除失敗");
        return;
      }

      toast.success("刪除成功");
      onDeleted();
    });
  });
}

function isStoredSetting(row) {
  return Number(row?.id) > 0;
}

function flagOn(value) {
  return Number(value) === 1;
}

function StatusChip({ on, onLabel, offLabel, onColor }) {
  return (
    <Chip
      size="small"
      label={on ? onLabel : offLabel}
      color={on ? onColor : "default"}
      variant={on ? "filled" : "outlined"}
    />
  );
}

const stickyActionSx = {
  position: "sticky",
  right: 0,
  zIndex: 1,
  bgcolor: "#fff",
  boxShadow: "-8px 0 8px -8px rgba(15, 23, 42, 0.28)",
};

export default function UserLeaderboardSettingListPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [skills, setSkills] = useState([]);
  const [previewCode, setPreviewCode] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  const loadList = useCallback(() => {
    setIsLoading(true);
    OrgAdminApi.getMyLeaderboardSettingListCallback((isSuccess, msg, data) => {
      if (!isSuccess) {
        toast.error(typeof msg === "string" && msg ? msg : "讀取排行榜設定失敗");
        setRows([]);
        setIsLoading(false);
        return;
      }

      setRows(Array.isArray(data) ? data : []);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    let active = true;
    InfoApi.getSkillList()
      .then((data) => {
        if (active) {
          setSkills(asList(data));
        }
      })
      .catch(() => {
        if (active) {
          setSkills([]);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const skillNameByCode = useMemo(() => {
    const map = {};
    skills.forEach((skill) => {
      const code = String(skill?.code || "").trim();
      if (code) {
        map[code] = skill.name_zh || skill.name_en || code;
      }
    });
    return map;
  }, [skills]);

  return (
    <Container maxWidth="xl">
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2, mb: 3 }}>
        <Typography variant="h4">排行榜設定</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => openLeaderboardDisplay()}>
            播放
          </Button>
          <Button variant="contained" onClick={() => navigate("/user/leaderboardSetting/create")}>
            建立排行榜
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ px: 3, pb: 3 }} elevation={3}>
        <TableContainer>
          <Table
            size="small"
            sx={{
              minWidth: 1280,
              borderCollapse: "separate",
              borderSpacing: 0,
              "& th, & td": { whiteSpace: "nowrap" },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell align="center">ID</TableCell>
                <TableCell align="center">標題</TableCell>
                <TableCell align="center">排名範圍</TableCell>
                <TableCell align="center">每一行代表</TableCell>
                <TableCell align="center">排名項目</TableCell>
                <TableCell align="center">背景</TableCell>
                <TableCell align="center">顯示順序</TableCell>
                {/* <TableCell align="center">是否生效</TableCell> */}
                <TableCell align="center">是否顯示</TableCell>
                <TableCell align="center" sx={{ ...stickyActionSx, zIndex: 2, bgcolor: "#f4f6f8" }}>
                  動作
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isLoading && rows.length === 0 ? (
                <TableRow>
                  <TableCell align="center" colSpan={9}>
                    未有排行榜設定
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const parsed = parseLeaderboardCode(row.leaderboard_code);
                  return (
                    <TableRow key={`${row.id}-${row.leaderboard_code}`}>
                      <TableCell align="center">{row.id || "-"}</TableCell>
                      <TableCell align="center">{row.title || "-"}</TableCell>
                      <TableCell align="center">{scopeLabel(parsed)}</TableCell>
                      <TableCell align="center">{groupByLabel(parsed)}</TableCell>
                      <TableCell align="center">{itemLabel(parsed)}</TableCell>
                      {/* <TableCell align="center">{skillLabel(parsed, skillNameByCode)}</TableCell> */}
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.75} justifyContent="center" alignItems="center">
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: "50%",
                              bgcolor: backgroundOption(row.background).color,
                              border: "1px solid rgba(17, 24, 39, 0.12)",
                              flexShrink: 0,
                            }}
                          />
                          <span>{backgroundOption(row.background).label}</span>
                        </Stack>
                      </TableCell>
                      <TableCell align="center">{row.display_pos}</TableCell>
                      {/* <TableCell align="center">
                        <StatusChip
                          on={flagOn(row.enable)}
                          onLabel="生效中"
                          offLabel="未生效"
                          onColor="success"
                        />
                      </TableCell> */}
                      <TableCell align="center">
                        <StatusChip
                          on={flagOn(row.display_slideshow)}
                          onLabel="顯示"
                          offLabel="不顯示"
                          onColor="info"
                        />
                      </TableCell>
                      <TableCell align="center" sx={stickyActionSx}>
                        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                          {isStoredSetting(row) ? (
                            <>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => navigate(`/user/leaderboardSetting/${row.id}`)}
                              >
                                編輯
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() => confirmDeleteLeaderboardSetting(row.id, loadList)}
                              >
                                刪除
                              </Button>
                            </>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              系統預設
                            </Typography>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!row.leaderboard_code}
                            onClick={() => openLeaderboardDisplay(row.leaderboard_code, row.background)}
                          >
                            預覽
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={!row.leaderboard_code}
                            onClick={() => {
                              setPreviewCode(row.leaderboard_code);
                              setPreviewOpen(true);
                            }}
                          >
                            查看數據
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <LeaderboardPreviewDialog
        open={previewOpen}
        code={previewCode}
        onClose={() => setPreviewOpen(false)}
      />
    </Container>
  );
}
